import { useState, useMemo } from 'react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { isEmpty } from '@frontend/validation';
import { Chip, Modal, ModalControlModalProps, Table, Text, Truncated, useAlert } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../constants';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { AppointmentTypesV3TrackingIds } from '../../trackingIds/appointment-types-v3-trackingids';
import { generateAppTypeColorAssignments } from '../AppointmentTypesV3/appointment-type-color-picker/appointment-type-color-palette';
import { AppointmentTypeColorPicker } from '../AppointmentTypesV3/appointment-type-color-picker/AppointmentTypeColorPicker';
import { ResponsiveModalFooter } from '../settings';

const trackingIds = AppointmentTypesV3TrackingIds;

interface Props extends ModalControlModalProps {
  isCalendarView: boolean;
  shouldShowLocationColumn?: boolean;
  appointmentTypes: AppointmentType[];
  isEdit?: boolean;
}

export const AppointmentTypesBulkColorAssignModal = ({
  isCalendarView,
  shouldShowLocationColumn,
  appointmentTypes,
  isEdit,
  ...modalProps
}: Props) => {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const { getScopeName } = useSchedulingLocationInfo();

  const { mutate: updateAppointmentTypesColors } = SchedulerV3.Mutations.useBulkUpdateAppointmentTypesColorsMutation({
    options: {
      onSuccess: () => {
        modalProps.onClose();
        alert.success(t('Colors assigned successfully.'));
      },
      onError: (error) => {
        console.error('Error updating appointment type colors:', error);
        alert.error(t('Failed to assign colors. Please try again.'));
      },
    },
  });

  // Local state to track color changes for each appointment type
  const [colorChanges, setColorChanges] = useState<Record<string, string>>({});

  useMemo(() => {
    if (!modalProps.show || !appointmentTypes.length || isEdit) return;
    setColorChanges(generateAppTypeColorAssignments(appointmentTypes));
  }, [appointmentTypes, modalProps.show, isEdit]);

  // Memoized data with applied color changes
  const appointmentTypesWithUpdatedColors = useMemo(() => {
    return appointmentTypes.map((appointmentType) => ({
      ...appointmentType,
      details: {
        ...appointmentType.details,
        clr: colorChanges[appointmentType.id] || appointmentType.details?.clr,
      },
    }));
  }, [appointmentTypes, colorChanges]);

  const handleColorChange = (appointmentTypeId: string, newColor: string) => {
    setColorChanges((prev) => ({
      ...prev,
      [appointmentTypeId]: newColor,
    }));
  };

  const handleApplyAssignments = () => {
    updateAppointmentTypesColors({
      appointmentTypesColors: Object.entries(colorChanges).map(([appointmentTypeId, color]) => ({
        appointmentTypeId,
        color,
      })),
    });
  };

  const handleResetColors = () => setColorChanges({});

  const hasChanges = !isEmpty(colorChanges);

  const modalTitle = isEdit ? t('Edit Appointment Type Colors') : t('Add Colors to Your Appointment Types');

  return (
    <Modal.Responsive {...modalProps} minWidth={500} maxWidth={600}>
      <Modal.Header onClose={modalProps.onClose} closeBtnTrackingId={trackingIds.bulkAssignmentModal.closeBtn}>
        {modalTitle}
      </Modal.Header>
      <Modal.Body>
        {isCalendarView && (
          <Text color='light' size='medium' weight='light'>
            {t('You can customize any of these suggestions or change individual appointment types later in settings.')}
          </Text>
        )}
        <Table
          colConfig={[
            {
              id: 'location',
              Header: t('Location'),
              accessor: 'locationId',
              cellRenderer: (locationId: string) => <Chip.Location>{getScopeName(locationId)}</Chip.Location>,
              maxWidth: 150,
              disableSortBy: true,
              omit: !shouldShowLocationColumn,
            },
            {
              id: 'appointmentType',
              Header: t('Appointment Type'),
              accessor: 'displayName',
              cellRenderer: (value: string) => <Truncated>{value}</Truncated>,
              minWidth: 300,
              startingSortBy: 'asc',
              sortType: 'caseInsensitive',
            },
            {
              id: 'color',
              Header: t('Color'),
              accessor: 'details.clr',
              cellRenderer: (value, row) => {
                const currentColor = colorChanges[row?.id || ''] || value || PRIMARY_EVENT_COLOR;
                return (
                  <AppointmentTypeColorPicker
                    color={currentColor}
                    onChange={(newColor) => handleColorChange(row?.id || '', newColor)}
                  />
                );
              },
              disableSortBy: true,
              maxWidth: 84,
            },
          ]}
          fullHeight
          fullHeightConfig={{ offset: 500 }}
          data={isEdit ? appointmentTypes : appointmentTypesWithUpdatedColors}
        />
      </Modal.Body>
      <ResponsiveModalFooter
        primary={{
          label: t('Apply Assignments'),
          onClick: handleApplyAssignments,
          trackingId: trackingIds.bulkAssignmentModal.applyAssignmentsBtn,
          disabled: !hasChanges,
        }}
        secondary={{
          label: t('Reset Colors'),
          onClick: handleResetColors,
          trackingId: trackingIds.bulkAssignmentModal.resetColorsBtn,
          disabled: !hasChanges,
        }}
      />
    </Modal.Responsive>
  );
};
